import { z } from "zod";
import { clockSchema } from "./settings.js";
// La sequenza di chiamate di un Lead come la vede la scheda (piano M5, Task 10a; D8–D10, D16): da quando e perché è partita, il
// tentativo di adesso con le chiamate fatte e quelle richieste, il prossimo dovuto, il giudizio («rispettata», «non rispettata» con
// il primo motivo), quando e perché è finita, le 14 caselle (una per chiamata richiesta dalla regola) e che cosa può fare chi
// guarda («Ferma la sequenza», «Riparti con la sequenza», «Il Lead ha risposto», con la modifica del Lead; nella «Guarda come…»
// nulla, come le chiamate, questione 164). La scheda del Lead la porta (`GET /api/leads/:leadId`, `sequence`, Task 11) e la
// comandano `POST /api/leads/:leadId/sequence/stop`, `…/restart` e `…/answered`, che rispondono la sequenza com'è dopo.
//
// Gli elenchi ripetono quelli del dominio (`@gioia365/domain`: `call-sequence.ts`), che i contratti non importano: un test del
// server li confronta voce per voce, e lo schema del database li lega al dominio con `everyValueOf`.
/** `open` in corso, `ended` finita. */
export const SEQUENCE_STATES = ["open", "ended"];
/** Perché è partita: il Lead nuovo, il Lead che rientra, «Riparti con la sequenza», la ripartenza al rientro dopo una sequenza esaurita, la ripartenza da sola dopo N giorni lavorativi senza contatto (SEQ-14). */
export const SEQUENCE_START_REASONS = ["lead_created", "lead_reentered", "restarted_manually", "restarted_on_reentry", "restarted_automatically"];
/** Perché è finita (D8). */
export const SEQUENCE_END_REASONS = [
    "answered",
    "answered_callback",
    "answered_whatsapp",
    "answered_manually",
    "booked",
    "wrong_number",
    "blocked_status",
    "exhausted",
    "stopped_manually",
    "archived",
    "merged",
];
/** Come ha risposto il Lead, per «Il Lead ha risposto»: «Su WhatsApp», «Ha richiamato», «In un altro modo». */
export const SEQUENCE_END_DETAILS = ["whatsapp", "called_back", "other"];
/** Il giudizio (D10): `pending` finché non c'è niente da giudicare. */
export const SEQUENCE_COMPLIANCES = ["pending", "respected", "violated"];
/** Lo stato di una casella, con la legenda «Da fare, Adesso, Fatta, Ha risposto, Non serve più, Non fatta». */
export const SEQUENCE_CALL_STATES = ["due", "now", "done", "answered", "not_needed", "not_made", "stopped"];
/**
 * Il primo motivo, in ordine di tempo, per cui la sequenza non è rispettata (`SequenceViolation` del dominio): la prima chiamata
 * in ritardo (i minuti e, per un Lead entrato fuori orario, l'apertura da cui si contano) o mai fatta (anche da quale mezza
 * giornata), un tentativo con meno chiamate di quelle richieste, una mezza giornata saltata (`AAAA-MM-GG:m|p`).
 */
export const violationSchema = z.discriminatedUnion("code", [
    z.object({ code: z.literal("first_call_late"), minutes: z.number().int(), afterOpening: z.string().nullable() }),
    z.object({ code: z.literal("first_call_missing"), minutes: z.number().int(), sinceHalfDay: z.string().nullable(), afterOpening: z.string().nullable() }),
    z.object({ code: z.literal("attempt_short"), attempt: z.number().int(), made: z.number().int(), required: z.number().int() }),
    z.object({ code: z.literal("half_day_missed"), halfDay: z.string() }),
]);
/** La sequenza di un Lead (l'ultima: in corso, oppure l'ultima finita). Istanti ISO; `version` per i comandi (`409 version_conflict`). */
export const callSequenceViewSchema = z.object({
    id: z.string(),
    state: z.enum(SEQUENCE_STATES),
    startedAt: z.string(),
    startReason: z.enum(SEQUENCE_START_REASONS),
    currentAttempt: z.number().int(),
    total: z.number().int(),
    madeInCurrent: z.number().int(),
    requiredInCurrent: z.number().int(),
    nextDueAt: z.string().nullable(),
    compliance: z.enum(SEQUENCE_COMPLIANCES),
    violation: violationSchema.nullable(),
    endedAt: z.string().nullable(),
    endReason: z.enum(SEQUENCE_END_REASONS).nullable(),
    endDetail: z.enum(SEQUENCE_END_DETAILS).nullable(),
    calls: z.array(z.object({ number: z.number().int(), attempt: z.number().int(), state: z.enum(SEQUENCE_CALL_STATES), callId: z.string().nullable(), at: z.string().nullable() })),
    permissions: z.object({ canStop: z.boolean(), canRestart: z.boolean(), canMarkAnswered: z.boolean() }),
    version: z.number().int(),
});
/**
 * Il posto di una chiamata nella sequenza (D9): il tentativo e il numero della chiamata (1–14 con la regola di partenza), scritti
 * quando la chiamata nasce. La telefonata della Timeline e la vista della chiamata lo portano per «tentativo N · chiamata M»;
 * `null` per una chiamata che non ha un posto o che non è arrivata al Lead (il suo posto l'ha ripreso la chiamata dopo).
 */
export const sequenceSlotSchema = z.object({ attempt: z.number().int(), callNumber: z.number().int() });
/** `POST /api/leads/:leadId/sequence/stop`, `…/restart` e `…/answered` (Task 11): la sequenza com'è dopo il comando. */
export const callSequenceResponseSchema = z.object({ sequence: callSequenceViewSchema });
/** «Il Lead ha risposto» (Task 11): come, e la versione della sequenza letta. */
export const markAnsweredBodySchema = z.strictObject({ how: z.enum(SEQUENCE_END_DETAILS), expectedVersion: z.number().int().min(1) });
/**
 * «Ferma la sequenza» e «Riparti con la sequenza» (Task 11): la versione della sequenza letta (quella della scheda: la sequenza in
 * corso o l'ultima finita). Per un Lead che una sequenza non l'ha mai avuta (questione 256) non c'è nulla da confrontare.
 */
export const sequenceActionBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1) });
// Impostazioni → «Sequenza di chiamate» (piano M5, Task 13, D11): la regola dell'azienda (`organization_settings.call_sequence`),
// letta da ogni persona attiva e cambiata da chi ha `settings.call_sequence`. Quante chiamate di fila in ogni tentativo (da 1 a 6,
// da 1 a 10 tentativi), entro quanti minuti la prima chiamata, il venerdì con un tentativo solo, dove finisce la mattina e comincia
// il pomeriggio, quanti secondi aspetta la cascata prima di chiamare e, dopo «Chiudi la chiamata», quanti secondi prima del Lead
// successivo (`null` è «lo passo io»; decisione del 9 ottobre 2026) e se una chiamata della cascata senza risposta si richiama da
// sola fino alle chiamate di fila del tentativo (`autoRedial`; decisione dell'11 ottobre 2026) e dopo quanti giorni lavorativi senza
// contatto una sequenza finita senza risposta riparte da capo da sola (`null` è «Mai»; SEQ-14). I confini delle mezze giornate
// rispetto al calendario dell'azienda (il pomeriggio comincia quando la mattina finisce o dopo, e nessun giorno aperto cade tutto
// dentro la pausa; ruling Q44) li controlla il server, che ha il calendario completato con l'orario di partenza (`422
// validation_failed` con i campi).
/** I minuti della prima chiamata che si possono scegliere. */
export const FIRST_CALL_MINUTES = [15, 30, 45, 60];
/** I secondi del conto alla rovescia della cascata che si possono scegliere: 0 è «niente, chiama subito». */
export const COUNTDOWN_SECONDS = [0, 2, 3, 5, 10];
/** I secondi dopo «Chiudi la chiamata» prima del Lead successivo che si possono scegliere: 0 è «subito». «Lo passo io» è `null`, non un numero. */
export const ADVANCE_SECONDS = [0, 3, 5, 10];
/** I giorni lavorativi senza contatto dopo cui una sequenza finita senza risposta riparte da capo (SEQ-14). «Mai» è `null`, non un numero. */
export const RESTART_AFTER_WORKING_DAYS = [5, 10, 14, 20, 30];
/** Le chiamate di fila di un tentativo, al più; i tentativi, al più. */
export const MAX_CALLS_PER_ATTEMPT = 6;
export const MAX_ATTEMPTS = 10;
export const callSequenceSettingsSchema = z.object({
    attempts: z.array(z.number().int().min(1).max(MAX_CALLS_PER_ATTEMPT)).min(1).max(MAX_ATTEMPTS),
    firstCallMinutes: z.union([z.literal(15), z.literal(30), z.literal(45), z.literal(60)]),
    fridayOne: z.boolean(),
    morningEnd: clockSchema,
    afternoonStart: clockSchema,
    countdownSeconds: z.union([z.literal(0), z.literal(2), z.literal(3), z.literal(5), z.literal(10)]),
    advanceSeconds: z.union([z.literal(0), z.literal(3), z.literal(5), z.literal(10), z.null()]),
    autoRedial: z.boolean(),
    restartAfterWorkingDays: z.union([z.literal(5), z.literal(10), z.literal(14), z.literal(20), z.literal(30), z.null()]),
});
/**
 * Un Lead che entra adesso, tentativo per tentativo (le mezze giornate del dominio, `halfDaysOf`): il giorno locale
 * (`AAAA-MM-GG`, nel fuso dell'azienda), quale metà e, per il primo, entro quando va fatta la prima chiamata (istante ISO).
 */
export const callSequenceExampleSchema = z.object({
    attempt: z.number().int(),
    halfDay: z.enum(["morning", "afternoon"]),
    day: z.string(),
    deadline: z.string().nullable(),
});
/**
 * `GET /api/settings/call-sequence` e la risposta di `PUT`: la regola (quella di partenza per un'azienda che non l'ha mai cambiata),
 * la versione delle impostazioni dell'azienda (la stessa del calendario: `409 version_conflict` se è cambiata), se chi chiede la
 * può cambiare, e l'esempio di un Lead che entra adesso.
 */
export const callSequenceSettingsResponseSchema = z.object({
    settings: callSequenceSettingsSchema,
    version: z.number().int(),
    canEdit: z.boolean(),
    example: z.array(callSequenceExampleSchema),
});
/** `PUT /api/settings/call-sequence`: la regola intera e la versione delle impostazioni letta. */
export const saveCallSequenceSettingsBodySchema = z.strictObject({ settings: callSequenceSettingsSchema, expectedVersion: z.number().int().min(0) });
//# sourceMappingURL=call-sequence.js.map